import mapValues from 'lodash-es/mapValues'

import { EMPTY_OBJECT } from '../../../constants/common'
import { LOCAL_STORAGE_BIRDVIEW_CHARTS_HIDDEN_KEY } from '../../../constants/storage'
import { getItemsIds, isValueNumberType } from '../../../utils/common'
import {
  BIRDVIEW_TILE_UNAVAILABLE_COUNT,
  BirdviewSection,
  EMPTY_SECTION,
} from '../DashboardBirdview.constants'
import { BIRDVIEW_CHART_DEFAULT_PERIOD } from '../DashboardBirdview.constants.controls'
import {
  checkIsBirdviewPersonalPreset,
  formatBirdviewKpiValue,
  formatBirdviewNumber,
  toBirdviewTileDelta,
} from '../DashboardBirdview.utils.charts'
import {
  getBirdviewLoadState,
  getBirdviewSectionState,
} from '../DashboardBirdview.utils.sections'
import {
  getBirdviewStorageItemSetKey,
  getBirdviewStorageKey,
} from '../DashboardBirdview.utils.storage'

import {
  BIRDVIEW_ANALYTICS_CHARTS,
  BIRDVIEW_ANALYTICS_SOON_CHARTS,
  BIRDVIEW_KPI_DEFINITIONS,
} from './BirdviewAnalytics.constants'

function getBirdviewChartData({ chart, section }) {
  return chart.dataKey ? section.data?.[chart.dataKey] : section.data
}

// The customer donuts share one section, each reads its split by `dataKey`.
function getBirdviewChartSection({ chart, section }) {
  const chartData = getBirdviewChartData({ chart, section }) ?? EMPTY_SECTION

  return {
    ...chartData,
    isRefreshing: section.isRefreshing,
    reload: section.reload,
    state: getBirdviewSectionState({
      hasError: section.hasError,
      isLoading: section.isLoading,
      isRefreshing: section.isRefreshing,
      items: chartData.items,
    }),
  }
}

function toBirdviewAnalyticsKpiValues({ definition, t, values }) {
  return {
    count: isValueNumberType(values.value)
      ? formatBirdviewKpiValue({
          t,
          unit: definition.unit,
          value: values.value,
        })
      : BIRDVIEW_TILE_UNAVAILABLE_COUNT,
    countTooltip: values.leftOut
      ? t(
          'DashboardBirdview.KpiOnTimeLeftOutKey',
          mapValues(values.leftOut, formatBirdviewNumber)
        )
      : null,
    delta: toBirdviewTileDelta({
      delta: values.delta,
      t,
      unit: definition.unit,
    }),
    deltaTone: values.tone,
    points: values.points,
    tone: values.tone,
  }
}

// A tile exists before its numbers arrive, so the row keeps its shape while
// the section loads or fails.
function toBirdviewAnalyticsKpi({
  definition,
  isRefreshing,
  state,
  t,
  values,
}) {
  return {
    ...definition,
    ...(values
      ? toBirdviewAnalyticsKpiValues({ definition, t, values })
      : null),
    info: definition.infoKey ? t(definition.infoKey) : null,
    isRefreshing,
    label: t(definition.labelKey),
    state,
  }
}

// The personal tiles count fixed windows ("this week"), so they have no period.
export function checkIsBirdviewAnalyticsPeriodAvailable(preset) {
  return !checkIsBirdviewPersonalPreset(preset)
}

export function getBirdviewAnalyticsCharts({ preset, sections, t }) {
  return BIRDVIEW_ANALYTICS_CHARTS[preset]
    .filter(chart => sections[chart.sectionKey])
    .map(chart => ({
      ...chart,
      info: chart.infoKey ? t(chart.infoKey) : null,
      section: getBirdviewChartSection({
        chart,
        section: sections[chart.sectionKey],
      }),
      title: t(chart.titleKey),
    }))
}

export function getBirdviewAnalyticsDefaultHiddenIds(charts) {
  return getItemsIds(charts.filter(chart => chart.isHiddenByDefault))
}

export function getBirdviewAnalyticsKpis({
  preset,
  section = EMPTY_OBJECT,
  t,
}) {
  const state = getBirdviewLoadState(section)

  return BIRDVIEW_KPI_DEFINITIONS[preset].map(definition =>
    toBirdviewAnalyticsKpi({
      definition,
      isRefreshing: section.isRefreshing,
      state,
      t,
      values: section.data?.[definition.id],
    })
  )
}

export function getBirdviewAnalyticsPeriod(chartControls) {
  return (
    chartControls[BirdviewSection.Kpis]?.period ??
    BIRDVIEW_CHART_DEFAULT_PERIOD.value
  )
}

export function getBirdviewAnalyticsHiddenStorageKey({ ids, storageScope }) {
  return getBirdviewStorageKey(LOCAL_STORAGE_BIRDVIEW_CHARTS_HIDDEN_KEY, [
    storageScope,
    getBirdviewStorageItemSetKey(ids),
  ])
}

export function getBirdviewAnalyticsCatalogRows({ charts, hiddenIds, t }) {
  return charts.map(chart => ({
    id: chart.id,
    isShown: !hiddenIds.includes(chart.id),
    object: chart.object,
    question: t(chart.questionKey),
    title: chart.title,
  }))
}

export function getBirdviewAnalyticsSoonRows({ preset, t }) {
  return BIRDVIEW_ANALYTICS_SOON_CHARTS[preset].map(chart => ({
    id: chart.id,
    isSoon: true,
    object: chart.object,
    question: t(chart.questionKey),
    title: t(chart.titleKey),
  }))
}

export function getBirdviewAnalyticsHiddenRows(rows) {
  return rows.filter(row => !row.isShown)
}
